import { useLocation, useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Switch,
  DropdownMenuItem,
  Badge,
} from "@/components/ui";
import { useIsMobile } from "@/hooks/use-mobile";
import { EyeIcon, MoreVertical, PencilIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { routes } from "@/routes/utils";
import { useGetStateLaws, useUpdateStateLawMutation } from "@/services";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { cn, ROLE_BOM, stateLawStatus, toApiSort } from "@/lib/utils";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

const useStateLaws = () => {
  const location = useLocation();
  const isOwner = location?.pathname?.includes("owner");
  const isMobile = useIsMobile();
  const [globalFilter, setGlobalFilter] = useState("");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "createdAt", desc: true }]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const masterDataPermission = useSelector((state: RootState) => state.currentData.masterDataPermission);
  const currentRole = useSelector((state: RootState) => state.currentData.currentRole);
  const isCamBom = location.pathname.startsWith("/cam") || location.pathname.startsWith("/bom") || (location.pathname.startsWith("/owner") && currentRole == ROLE_BOM);

  const hasAddPermission = useMemo(() => isCamBom ? masterDataPermission?.add : true, [masterDataPermission]);
  // const hasDeletePermission = useMemo(() => isCamBom ? masterDataPermission?.delete : true, [masterDataPermission]);
  const hasUpdatePermission = useMemo(() => isCamBom ? masterDataPermission?.update : true, [masterDataPermission]);

  const {
    data: stateLawList,
    isLoading: isStateLawLoading,
    refetch: refetchStateLaw,
    isRefetching: isRefetchingStateLaw,
  } = useGetStateLaws({
    data: {
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "createdAt", direction: "DESC" }),
    },
    queryParams: { refetchOnWindowFocus: false, refetchOnReconnect: false },
  });

  const { mutate: updateStateLawInfo, isPending: isUpdateStateLawPending } =
    useUpdateStateLawMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Status Updated Successfully");
        queryClient.invalidateQueries({
          queryKey: ["state-law"],
        });
      },
    });

  useEffect(() => {
    refetchStateLaw();
  }, [pageNumber, pageSize, sorting]);

  useEffect(() => {
    if (globalFilter?.length >= 2 || globalFilter?.length == 0) {
      refetchStateLaw();
    }
  }, [globalFilter]);

  // Define Table Columns
  const columns = [
    {
      accessorKey: "stateName",
      header: "State Name",
      accessorFn: (row: any) => String(row?.stateName || ""),
    },
    {
      accessorKey: "createdAt",
      header: "Created On",
      accessorFn: (row: any) =>
        row?.createdAt ? moment(row?.createdAt).format("MM/DD/YYYY") : "",
    },
    {
      accessorKey: "updatedAt",
      header: "Updated On",
      accessorFn: (row: any) =>
        row?.updatedAt ? moment(row?.updatedAt).format("MM/DD/YYYY") : "",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (row: any) => {
        return (
          <Badge
            variant="outline"
            className={cn(
              (stateLawStatus as any)[row.row.original.status?.toLowerCase()]
                ?.color,
              "rounded-full px-4 py-2"
            )}
          >
            {
              (stateLawStatus as any)[row.row.original.status?.toLowerCase()]
                ?.label
            }
          </Badge>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Active/Inactive",
      cell: (row: any) => (
        <div
          className="flex items-center space-x-2"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Switch
            id="active"
            disabled={isUpdateStateLawPending || row.row.original.status == stateLawStatus.published.value}
            onCheckedChange={(isActive: any) => {
              updateStateLawInfo({
                stateLawId: row?.row?.original?.stateLawBlockId,
                data: {
                  ...row?.row?.original,
                  status: isActive ? "PUBLISHED" : "DRAFT",
                },
              });
            }}
            checked={row.row.original.status == stateLawStatus.published.value}
          />
        </div>
      ),
    },
    ...(isOwner
      ? []
      : [
          {
            id: "actions",
            header: "Actions",
            enableSorting: false,
            cell: (row: any) => {
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger>
                    <MoreVertical className="w-5 h-5 cursor-pointer" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="bg-white border-0 shadow-md"
                  >
                    <DropdownMenuItem
                      className="flex items-center gap-2"
                      disabled={isUpdateStateLawPending}
                      onClick={() => {
                        navigate(`${row.row.original?.stateLawBlockId}/view`);
                      }}
                    >
                      <EyeIcon className="w-4 h-4" />
                      View
                    </DropdownMenuItem>
                    {hasUpdatePermission && <DropdownMenuItem
                      className="flex items-center gap-2"
                      onClick={() => {
                        navigate(`${row.row.original?.stateLawBlockId}/edit`);
                      }}
                    >
                      <PencilIcon className="w-4 h-4" />
                      Edit
                    </DropdownMenuItem>}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            },
          },
        ]),
  ];

  const onAdd = () => {
    navigate(`${routes.add}`);
  };

  return {
    onAdd,
    isMobile,
    globalFilter,
    setGlobalFilter,
    columns,
    stateLawList,
    isStateLawLoading,
    isRefetchingStateLaw,
    openDeleteDialog,
    setOpenDeleteDialog,
    isOwner,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    sorting,
    setSorting,
    hasAddPermission
  };
};
export default useStateLaws;
